import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react'

import intro1 from '@/assets/mocks/intro-1.webp'
import intro2 from '@/assets/mocks/intro-2.webp'
import { CarouselDots } from '@/components/widgets/CarouselDots'

import { IntroSlide3 } from './IntroSlide3'
import { IntroSlideImage } from './IntroSlideImage'

import './styles.scss'

type IntroSlideId = 'scenario-library' | 'practice-with-agents' | 'feedback'

interface IntroSlide {
  id: IntroSlideId
  title: string
  variant: 'light' | 'dark'
  content: ReactNode
}

const allSlides: IntroSlide[] = [
  {
    id: 'scenario-library',
    title: 'Choose scenarios you want to practice from scenario library',
    variant: 'light',
    content: <IntroSlideImage src={intro1} alt="Choose scenarios from library" />,
  },
  {
    id: 'practice-with-agents',
    title: 'Practice different scenarios with AI agents based on real life situations',
    variant: 'light',
    content: <IntroSlideImage src={intro2} alt="Practice with AI agents" />,
  },
  {
    id: 'feedback',
    title: 'Get feedback, reflect and improve your communication skills',
    variant: 'dark',
    content: <IntroSlide3 />,
  },
]

interface IntroCarouselProps {
  autoPlayInterval?: number
}

export function IntroCarousel({ autoPlayInterval = 5000 }: IntroCarouselProps) {
  const slides = allSlides
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const totalSlides = slides.length
  const autoPlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastInteractionTime = useRef<number>(0)

  const startAutoPlay = useCallback(() => {
    if (totalSlides <= 1 || autoPlayInterval <= 0) {
      return
    }

    // Clear any existing timer
    if (autoPlayTimerRef.current) {
      clearInterval(autoPlayTimerRef.current)
    }

    autoPlayTimerRef.current = setInterval(() => {
      // Check if enough time has passed since last interaction
      const now = Date.now()
      if (now - lastInteractionTime.current < autoPlayInterval) {
        return
      }

      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentIndex(prevIndex => (prevIndex + 1) % totalSlides)
        setIsTransitioning(false)
      }, 150)
    }, autoPlayInterval)
  }, [totalSlides, autoPlayInterval])

  useEffect(() => {
    startAutoPlay()
    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current)
      }
    }
  }, [startAutoPlay])

  const handleDotClick = useCallback(
    (index: number) => {
      if (index !== currentIndex) {
        // Record interaction time
        lastInteractionTime.current = Date.now()

        setIsTransitioning(true)
        setTimeout(() => {
          setCurrentIndex(index)
          setIsTransitioning(false)
        }, 150)

        // Reset the autoplay timer
        startAutoPlay()
      }
    },
    [currentIndex, startAutoPlay],
  )

  const currentSlide = slides[currentIndex]
  if (!currentSlide) return null

  const footerVariant = currentSlide.variant === 'dark' ? 'dark' : 'light'

  return (
    <div className={`intro-carousel intro-carousel--${currentSlide.variant}`}>
      <div className="intro-carousel__content">
        <div className={`intro-carousel__slide ${isTransitioning ? 'intro-carousel__slide--transitioning' : ''}`}>
          {currentSlide.content}
        </div>

        <div className="intro-carousel__footer">
          <h2 className="intro-carousel__title">{currentSlide.title}</h2>
          <CarouselDots
            total={totalSlides}
            activeIndex={currentIndex}
            variant={footerVariant}
            onDotClick={handleDotClick}
          />
        </div>
      </div>
    </div>
  )
}
